import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import * as clientActions from "../../redux/actions/clientActions";
import * as clientLedgerActions from "../../redux/actions/clientLedgerActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import SettingsService from "../../services/SettingsService";
import { CLIENT_LEDGER_ENTRY_TYPES } from "../manage/clientLedgerTypes";
import ClientSettlementsReportForm from "./ClientSettlementsReportForm";
import ClientSettlementsList from "./ClientSettlementsList";
import ClientPaymentsModal from "./ClientPaymentsModal";
import buildSchoolYearMonthOptions from "./clientSettlementsMonthOptions";
import { buildMonthlyChargeEntry, calculateClientSettlements, collectMissingClientWarnings, recalcWithExclusions } from "./clientLedgerCalculations";
import { getMonthlyClientPayments } from "./clientSettlementPayments";
import {
  enrichSettlementsWithWatchdog,
  filterWatchdogSettlements,
  getPaymentWatchdogFollowUpKey,
  PAYMENT_WATCHDOG_FOLLOW_UP_STATUSES,
  PAYMENT_WATCHDOG_SETTING_ID,
} from "./clientSettlementWatchdog";
import { buildSchoolYearRange, getCurrentSchoolYearStartYear } from "../common/schoolYear";

const currentMonth = () => {
  const currentDate = new Date();
  return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
};

const getDefaultSettlementMonth = (activeSchoolYearStartYear, today = new Date()) => {
  const currentSchoolYearStartYear = getCurrentSchoolYearStartYear(today);

  if (activeSchoolYearStartYear === currentSchoolYearStartYear) {
    return currentMonth();
  }

  return `${activeSchoolYearStartYear + 1}-08`;
};

const makeKey = (clientId, itemKey) => `${clientId}#${itemKey}`;
const clientKeyPrefix = clientId => `${clientId}#`;

const stringifyWarningRecord = record => (record ? JSON.stringify(record) : "brak");
const getMissingClientWarningKey = warning => `${warning.lessonRegisterId}#${warning.studentId}`;
const SORT_DIRECTION = {
  ASCENDING: "ascending",
  DESCENDING: "descending",
};

const DEFAULT_SORT_CONFIG = {
  key: "closingBalance",
  direction: SORT_DIRECTION.ASCENDING,
};

const DEFAULT_WATCHDOG_FILTERS = {
  enabledOnly: false,
  minAmount: "",
  minOverdueDays: "",
  priority: "",
  followUpStatus: "",
};

const getPaymentWatchdogCases = settings => {
  const settingsList = Array.isArray(settings) ? settings : [settings];
  const watchdogSetting = settingsList.find(setting => setting && setting.id === PAYMENT_WATCHDOG_SETTING_ID);
  return watchdogSetting && watchdogSetting.cases ? watchdogSetting.cases : {};
};

const isSettlementEmpty = settlement => {
  const snapshot = settlement.snapshot || {};
  const hasLessons = (snapshot.charged || []).length > 0 || (snapshot.skipped || []).length > 0;
  const hasWarnings = (snapshot.warnings || []).length > 0;
  const hasFinancialActivity = [
    settlement.openingBalance,
    settlement.paymentsInMonth,
    settlement.monthlyChargeAmount,
    settlement.closingBalance,
  ].some(value => Number(value || 0) !== 0);

  return !hasLessons && !hasWarnings && !hasFinancialActivity;
};

const matchesWorkflowFilter = (settlement, report) => {
  if (report.showDraft === report.showApproved) return true;
  if (report.showDraft) return !settlement.approved;
  return settlement.approved;
};

const matchesNegativeBalanceFilter = (settlement, report) => (
  !report.showNegativeBalanceOnly || Number(settlement.closingBalance || 0) < 0
);

const getSettlementStatusLabel = settlement => (settlement.approved ? "Zatwierdzone" : "Robocze");

const compareStrings = (left, right, direction) => {
  const comparison = String(left || "").toLowerCase().localeCompare(String(right || "").toLowerCase());
  return direction === SORT_DIRECTION.ASCENDING ? comparison : -comparison;
};

const compareNumbers = (left, right, direction) => {
  const delta = Number(left || 0) - Number(right || 0);
  return direction === SORT_DIRECTION.ASCENDING ? delta : -delta;
};

const compareDisplaySettlements = (left, right, sortConfig) => {
  switch (sortConfig.key) {
    case "clientName":
      return compareStrings(left.clientName, right.clientName, sortConfig.direction);
    case "openingBalance":
      return compareNumbers(left.openingBalance, right.openingBalance, sortConfig.direction);
    case "paymentsInMonth":
      return compareNumbers(left.paymentsInMonth, right.paymentsInMonth, sortConfig.direction);
    case "monthlyChargeAmount":
      return compareNumbers(left.summaryMonthlyChargeAmount, right.summaryMonthlyChargeAmount, sortConfig.direction);
    case "status":
      return compareStrings(left.statusLabel, right.statusLabel, sortConfig.direction);
    case "closingBalance":
    default:
      return compareNumbers(left.summaryClosingBalance, right.summaryClosingBalance, sortConfig.direction);
  }
};

export function ClientSettlementsReportPage({
  clients,
  students,
  teachers,
  schoolClasses,
  lessonRegister,
  clientLedger,
  clientOptions,
  loadClients,
  loadStudents,
  loadTeachers,
  loadSchoolClasses,
  loadLessonRegisters,
  loadClientLedger,
  saveClientLedgerEntry,
  deleteClientLedgerEntry,
  replaceMonthlyCharge,
  activeSchoolYearStartYear,
  settingsService,
}) {
  const activeSchoolYearRange = useMemo(
    () => buildSchoolYearRange(activeSchoolYearStartYear),
    [activeSchoolYearStartYear],
  );
  const monthOptions = useMemo(
    () => buildSchoolYearMonthOptions(activeSchoolYearRange),
    [activeSchoolYearRange],
  );
  const [report, setReport] = useState({
    month: getDefaultSettlementMonth(activeSchoolYearStartYear),
    clientId: "all",
    showDraft: false,
    showApproved: false,
    showNegativeBalanceOnly: false,
    showEmptyClients: false,
    watchdog: { ...DEFAULT_WATCHDOG_FILTERS },
    sortConfig: { ...DEFAULT_SORT_CONFIG },
  });
  const [excludedKeys, setExcludedKeys] = useState({});
  const [paymentsModal, setPaymentsModal] = useState({
    isOpen: false,
    clientId: "",
    month: "",
  });
  const [warningsExpanded, setWarningsExpanded] = useState(false);
  const [watchdogFollowUps, setWatchdogFollowUps] = useState({});
  const [watchdogDrafts, setWatchdogDrafts] = useState({});
  const [watchdogMessage, setWatchdogMessage] = useState("");
  const [watchdogError, setWatchdogError] = useState("");

  useEffect(() => {
    setReport(currentReport => ({
      ...currentReport,
      month: getDefaultSettlementMonth(activeSchoolYearStartYear),
    }));
  }, [activeSchoolYearStartYear]);

  useEffect(() => {
    if (clients.length === 0) {
      loadClients().catch(error => alert(`Loading clients failed ${error}`));
    }
    if (students.length === 0) {
      loadStudents().catch(error => alert(`Loading students failed ${error}`));
    }
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
      .catch(error => alert(`Loading schoolClasses failed ${error}`));
    if (teachers.length === 0) {
      loadTeachers().catch(error => alert(`Loading teachers failed ${error}`));
    }
  }, [
    clients.length,
    loadClients,
    loadSchoolClasses,
    loadStudents,
    loadTeachers,
    students.length,
    teachers.length,
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
  ]);

  useEffect(() => {
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
      .catch(error => alert(`Loading lessonRegisters failed ${error}`));
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    loadLessonRegisters,
  ]);

  useEffect(() => {
    loadClientLedger().catch(error => alert(`Loading clientLedger failed ${error}`));
  }, [loadClientLedger]);

  useEffect(() => {
    Promise.resolve()
      .then(() => settingsService.getSettings())
      .then(settings => {
        const followUps = getPaymentWatchdogCases(settings);
        setWatchdogFollowUps(followUps);
        setWatchdogDrafts({});
      })
      .catch(error => {
        if (typeof fetch === "undefined" && error && error.name === "ReferenceError") {
          return;
        }
        setWatchdogError("Nie udalo sie wczytac statusow watchdog.");
      });
  }, [settingsService]);

  const baseSettlements = useMemo(() => calculateClientSettlements({
    clients,
    month: report.month,
    ledger: clientLedger,
    lessonRegister,
    students,
    schoolClasses,
    teachers,
  }), [clientLedger, clients, lessonRegister, report.month, schoolClasses, students, teachers]);
  const settlementWorkflowSignature = useMemo(() => baseSettlements
    .map(settlement => `${settlement.clientId}:${settlement.approved ? "approved" : "draft"}`)
    .sort()
    .join("|"), [baseSettlements]);

  const filteredSettlements = useMemo(() => baseSettlements
    .filter(settlement => report.clientId === "all" || settlement.clientId === report.clientId)
    .filter(settlement => matchesWorkflowFilter(settlement, report))
    .filter(settlement => matchesNegativeBalanceFilter(settlement, report))
    .filter(settlement => report.showEmptyClients || !isSettlementEmpty(settlement)), [
    baseSettlements,
    report.clientId,
    report.showDraft,
    report.showApproved,
    report.showNegativeBalanceOnly,
    report.showEmptyClients,
  ]);
  const reportWarnings = useMemo(() => collectMissingClientWarnings({
    month: report.month,
    lessonRegister,
    students,
  }), [lessonRegister, report.month, students]);
  const expansionResetKey = [
    report.month,
    report.clientId,
    report.showDraft,
    report.showApproved,
    report.showNegativeBalanceOnly,
    report.showEmptyClients,
    JSON.stringify(report.watchdog || {}),
    report.sortConfig.key,
    report.sortConfig.direction,
    settlementWorkflowSignature,
  ].join(":");

  const baseDisplaySettlements = useMemo(() => filteredSettlements
    .map(savedSettlement => {
      const previewSettlement = recalcWithExclusions(savedSettlement, report.month, excludedKeys);
      const sanitizedWarnings = (previewSettlement.snapshot.warnings || []).filter(warning => warning.reason !== "missingClient");
      const sanitizedPreviewSettlement = {
        ...previewSettlement,
        snapshot: {
          ...previewSettlement.snapshot,
          warnings: sanitizedWarnings,
        },
      };

      return {
        ...savedSettlement,
        statusLabel: getSettlementStatusLabel(savedSettlement),
        summaryMonthlyChargeAmount: savedSettlement.monthlyChargeAmount,
        summaryClosingBalance: savedSettlement.closingBalance,
        previewMonthlyChargeAmount: sanitizedPreviewSettlement.monthlyChargeAmount,
        previewClosingBalance: sanitizedPreviewSettlement.closingBalance,
        previewSnapshot: sanitizedPreviewSettlement.snapshot,
        previewSettlement: sanitizedPreviewSettlement,
      };
    })
    .sort((left, right) => compareDisplaySettlements(left, right, report.sortConfig)), [
    excludedKeys,
    filteredSettlements,
    report.month,
    report.sortConfig,
  ]);
  const enrichedSettlements = useMemo(() => enrichSettlementsWithWatchdog({
    settlements: baseDisplaySettlements,
    followUps: watchdogFollowUps,
    now: new Date(),
  }), [baseDisplaySettlements, watchdogFollowUps]);
  const watchdogFilteredSettlements = useMemo(() => filterWatchdogSettlements(
    enrichedSettlements,
    report.watchdog,
  ), [enrichedSettlements, report.watchdog]);

  const selectedClient = useMemo(() => clients.find(client => client.id === paymentsModal.clientId), [clients, paymentsModal.clientId]);
  const modalEntries = useMemo(() => (
    paymentsModal.isOpen
      ? getMonthlyClientPayments(clientLedger, paymentsModal.clientId, paymentsModal.month)
      : []
  ), [clientLedger, paymentsModal.clientId, paymentsModal.isOpen, paymentsModal.month]);

  const handleChange = event => {
    const { name, value } = event.target;

    if (name.startsWith("watchdog.")) {
      const watchdogField = name.split(".")[1];
      setReport(currentReport => ({
        ...currentReport,
        watchdog: {
          ...currentReport.watchdog,
          [watchdogField]: value,
        },
      }));
      return;
    }

    setReport(currentReport => ({
      ...currentReport,
      [name]: value,
    }));
  };

  const handleSort = key => {
    setReport(currentReport => {
      const isSameKey = currentReport.sortConfig.key === key;
      const direction = isSameKey && currentReport.sortConfig.direction === SORT_DIRECTION.ASCENDING
        ? SORT_DIRECTION.DESCENDING
        : SORT_DIRECTION.ASCENDING;

      return {
        ...currentReport,
        sortConfig: {
          key,
          direction,
        },
      };
    });
  };

  const handleClearFilters = () => {
    setReport(currentReport => ({
      ...currentReport,
      clientId: "all",
      showDraft: false,
      showApproved: false,
      showNegativeBalanceOnly: false,
      showEmptyClients: false,
      watchdog: { ...DEFAULT_WATCHDOG_FILTERS },
      sortConfig: { ...DEFAULT_SORT_CONFIG },
    }));
  };

  useEffect(() => {
    if (paymentsModal.isOpen) {
      setPaymentsModal({
        isOpen: false,
        clientId: "",
        month: "",
      });
    }
  }, [report.clientId, report.month]);

  useEffect(() => {
    setWarningsExpanded(false);
  }, [report.clientId, report.month]);

  const handleToggleExclusion = (clientId, itemKey) => {
    const key = makeKey(clientId, itemKey);
    setExcludedKeys(prev => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  const handleApprove = settlement => {
    const existingEntry = clientLedger.find(entry => (
      entry.clientId === settlement.clientId &&
      entry.type === CLIENT_LEDGER_ENTRY_TYPES.MONTHLY_CHARGE &&
      entry.settlementMonth === settlement.month
    ));
    const blockingWarnings = (settlement.snapshot.warnings || []).filter(warning => warning.reason === "missingRate");

    if (blockingWarnings.length > 0) {
      alert("Nie mozna zatwierdzic rozliczenia bez zdefiniowanej stawki.");
      return;
    }

    const entry = buildMonthlyChargeEntry(settlement);
    replaceMonthlyCharge(existingEntry, entry)
      .then(() => {
        setExcludedKeys(currentKeys => Object.keys(currentKeys)
          .filter(key => !key.startsWith(clientKeyPrefix(settlement.clientId)))
          .reduce((nextKeys, key) => ({
            ...nextKeys,
            [key]: currentKeys[key],
          }), {}));
        alert("Rozliczenie zatwierdzone.");
      })
      .catch(error => {
        alert(`Nie mozna zatwierdzic rozliczenia: ${error.message || error}`);
      });
  };

  const handleOpenPayments = (clientId, month) => {
    setPaymentsModal({
      isOpen: true,
      clientId,
      month,
    });
  };

  const handleClosePayments = () => {
    setPaymentsModal({
      isOpen: false,
      clientId: "",
      month: "",
    });
  };

  const handleWatchdogDraftChange = (settlement, patch) => {
    const key = getPaymentWatchdogFollowUpKey(settlement.clientId, settlement.month);
    setWatchdogDrafts(currentDrafts => ({
      ...currentDrafts,
      [key]: {
        ...(currentDrafts[key] || {}),
        ...patch,
      },
    }));
  };

  const handleSaveWatchdogFollowUp = settlement => {
    const key = getPaymentWatchdogFollowUpKey(settlement.clientId, settlement.month);
    const existingRecord = watchdogFollowUps[key] || {};
    const draftRecord = watchdogDrafts[key] || {};
    const updatedAt = new Date().toISOString();
    const buildNextRecord = latestRecord => {
      const status = draftRecord.status || latestRecord.status || PAYMENT_WATCHDOG_FOLLOW_UP_STATUSES.TO_CONTACT;
      const note = draftRecord.note !== undefined ? draftRecord.note : latestRecord.note || "";

      return {
        ...latestRecord,
        ...draftRecord,
        clientId: settlement.clientId,
        settlementMonth: settlement.month,
        status,
        note,
        updatedAt,
        history: [
          ...(latestRecord.history || []),
          {
            status,
            note,
            updatedAt,
          },
        ],
      };
    };
    let fallbackFollowUps = null;

    setWatchdogError("");
    setWatchdogMessage("");

    return settingsService.getSettings()
      .then(settings => getPaymentWatchdogCases(settings))
      .then(latestFollowUps => {
        const nextRecord = buildNextRecord(latestFollowUps[key] || existingRecord);
        const nextFollowUps = {
          ...latestFollowUps,
          [key]: nextRecord,
        };
        fallbackFollowUps = nextFollowUps;

        return settingsService.saveSetting({
          id: PAYMENT_WATCHDOG_SETTING_ID,
          cases: nextFollowUps,
        });
      })
      .then(savedSetting => {
        const savedFollowUps = savedSetting && savedSetting.cases ? savedSetting.cases : fallbackFollowUps;
        setWatchdogFollowUps(savedFollowUps);
        setWatchdogDrafts(currentDrafts => {
          const nextDrafts = { ...currentDrafts };
          delete nextDrafts[key];
          return nextDrafts;
        });
        setWatchdogMessage("Follow-up zapisany.");
      })
      .catch(() => {
        setWatchdogError("Nie udalo sie zapisac follow-up.");
      });
  };

  return (
    <div className="mb-5">
      <h2 className="manage-title">Rozliczenia klientow</h2>
      {watchdogMessage && <div className="alert alert-success" role="alert">{watchdogMessage}</div>}
      {watchdogError && <div className="alert alert-danger" role="alert">{watchdogError}</div>}
      <ClientSettlementsReportForm
        month={report.month}
        monthOptions={monthOptions}
        clientId={report.clientId}
        clientOptions={clientOptions}
        showDraft={report.showDraft}
        showApproved={report.showApproved}
        showNegativeBalanceOnly={report.showNegativeBalanceOnly}
        showEmptyClients={report.showEmptyClients}
        watchdog={report.watchdog}
        onChange={handleChange}
        onClearFilters={handleClearFilters}
      />
      <div className="manage-text mb-3">
        Widocznych klientow: {watchdogFilteredSettlements.length}
      </div>
      {reportWarnings.length > 0 && (
        <div className="alert alert-warning manage-text mt-3" role="alert">
          <div className="d-flex justify-content-between align-items-center">
            <div className="font-weight-bold">
              <i className="fas fa-exclamation-triangle mr-2" aria-hidden="true" />
              Wykryto problemy z przypisaniem uczniow do klientow.
            </div>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setWarningsExpanded(currentState => !currentState)}
              aria-expanded={warningsExpanded}
            >
              {warningsExpanded ? "Ukryj ostrzezenia" : `Ostrzezenia rozliczen (${reportWarnings.length})`}
            </button>
          </div>
          {warningsExpanded && (
            <div className="mt-3">
              {reportWarnings.map(warning => (
                <div key={getMissingClientWarningKey(warning)} className="mb-3">
                  <div>Data: {warning.date} {warning.startTime || ""}</div>
                  <div>lessonRegisterId: {warning.lessonRegisterId}</div>
                  <div>lessonId: {warning.lessonId}</div>
                  <div>studentId: {warning.studentId}</div>
                  <div>
                    Uczen: {warning.studentRecord ? `${warning.studentRecord.firstName || ""} ${warning.studentRecord.lastName || ""}`.trim() || "brak" : "brak"}
                  </div>
                  <div>Powiazany clientId: {warning.studentRecord && warning.studentRecord.clientId ? warning.studentRecord.clientId : "brak"}</div>
                  <div>Rekord students: {stringifyWarningRecord(warning.studentRecord)}</div>
                  <div>Wpis lessonregister.students: {stringifyWarningRecord(warning.lessonStudent)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {watchdogFilteredSettlements.length > 0 ? (
        <ClientSettlementsList
          settlements={watchdogFilteredSettlements}
          sortConfig={report.sortConfig}
          expansionResetKey={expansionResetKey}
          onSort={handleSort}
          onToggleExclusion={handleToggleExclusion}
          onApprove={handleApprove}
          onOpenPayments={handleOpenPayments}
          watchdogDrafts={watchdogDrafts}
          onWatchdogDraftChange={handleWatchdogDraftChange}
          onSaveWatchdogFollowUp={handleSaveWatchdogFollowUp}
        />
      ) : (
        <div className="alert alert-light border manage-text">
          Brak klientow spelniajacych wybrane filtry.
        </div>
      )}
      <ClientPaymentsModal
        show={paymentsModal.isOpen}
        clientName={selectedClient ? `${selectedClient.firstName || ""} ${selectedClient.lastName || ""}`.trim() : ""}
        clientId={paymentsModal.clientId}
        month={paymentsModal.month || report.month}
        entries={modalEntries}
        onClose={handleClosePayments}
        onSave={saveClientLedgerEntry}
        onDelete={deleteClientLedgerEntry}
      />
    </div>
  );
}

ClientSettlementsReportPage.propTypes = {
  clients: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  teachers: PropTypes.array,
  schoolClasses: PropTypes.array.isRequired,
  lessonRegister: PropTypes.array.isRequired,
  clientLedger: PropTypes.array.isRequired,
  clientOptions: PropTypes.array,
  loadClients: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  loadTeachers: PropTypes.func,
  loadSchoolClasses: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  loadClientLedger: PropTypes.func.isRequired,
  saveClientLedgerEntry: PropTypes.func,
  deleteClientLedgerEntry: PropTypes.func,
  replaceMonthlyCharge: PropTypes.func.isRequired,
  activeSchoolYearStartYear: PropTypes.number,
  settingsService: PropTypes.shape({
    getSettings: PropTypes.func.isRequired,
    saveSetting: PropTypes.func.isRequired,
  }),
};

ClientSettlementsReportPage.defaultProps = {
  teachers: [],
  clientOptions: [{ value: "all", text: "Wszyscy" }],
  loadTeachers: () => Promise.resolve(),
  saveClientLedgerEntry: () => Promise.resolve(),
  deleteClientLedgerEntry: () => Promise.resolve(),
  activeSchoolYearStartYear: getCurrentSchoolYearStartYear(),
  settingsService: SettingsService,
};

const mapStateToProps = state => ({
  clients: state.clients,
  students: state.students,
  teachers: state.teachers,
  schoolClasses: state.schoolClasses,
  lessonRegister: state.lessonRegister,
  clientLedger: state.clientLedger,
  clientOptions: [
    { value: "all", text: "Wszyscy" },
    ...(state.clients || [])
      .filter(client => !client.deleted)
      .map(client => ({
        value: client.id,
        text: `${client.firstName || ""} ${client.lastName || ""}`.trim(),
      })),
  ],
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
});

export default connect(mapStateToProps, {
  loadClients: clientActions.loadClients,
  loadStudents: studentActions.loadStudents,
  loadTeachers: teacherActions.loadTeachers,
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  loadClientLedger: clientLedgerActions.loadClientLedger,
  saveClientLedgerEntry: clientLedgerActions.saveClientLedgerEntry,
  deleteClientLedgerEntry: clientLedgerActions.deleteClientLedgerEntry,
  replaceMonthlyCharge: clientLedgerActions.replaceMonthlyCharge,
})(ClientSettlementsReportPage);
